import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, resolvePath, type NavigateOptions, type To } from 'react-router-dom';
import { mobileDestinationMessage, resolveMobileDestination } from './navigation';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';

type DraftGuard = { title?: string; description?: string; saveError?: string; dirty: boolean; saving: boolean; save: () => Promise<boolean>; discard: () => void };
type GuardContext = { register: (guard: DraftGuard) => () => void; request: (action: () => void) => void; unavailable: (target: string) => void };
const Context = createContext<GuardContext | null>(null);

/** Mounted only by the mobile shell. Desktop navigation never registers a guard. */
export function MobileNavigationGuard({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const availabilityTitleId = React.useId();
  const [unavailableTarget, setUnavailableTarget] = useState<string | null>(null);
  const guards = useRef<DraftGuard[]>([]);
  const completedGuards = useRef(new Set<DraftGuard>());
  const [activeGuard, setActiveGuard] = useState<DraftGuard | null>(null);
  const [pending, setPending] = useState<(() => void) | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const register = useCallback((guard: DraftGuard) => {
    guards.current.push(guard);
    return () => { guards.current = guards.current.filter((entry) => entry !== guard); };
  }, []);
  const request = useCallback((action: () => void, completed?: Set<DraftGuard>) => {
    if (!completed) completedGuards.current.clear();
    const guard = [...guards.current].reverse().find((entry) => !completed?.has(entry) && (entry.dirty || entry.saving));
    if (guard) {
      setActiveGuard(guard);
      setError(null);
      setPending(() => action);
    } else action();
  }, []);
  const leave = async (save: boolean) => {
    if (!pending || busy || activeGuard?.saving) return;
    setBusy(true);
    setError(null);
    try {
      if (save && activeGuard && !await activeGuard.save()) {
        setError(activeGuard?.saveError ?? 'Unable to save. Stay here to correct the billing items or try again.');
        return;
      }
      if (!save) activeGuard?.discard();
      const action = pending;
      setPending(null);
      if (activeGuard) completedGuards.current.add(activeGuard);
      request(action, completedGuards.current);
    } catch {
      setError(activeGuard?.saveError ?? 'Unable to save. Your billing draft is still here.');
    } finally { setBusy(false); }
  };
  useEffect(() => {
    const onUnload = (event: BeforeUnloadEvent) => {
      if (!guards.current.some((guard) => guard.dirty)) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, []);
  // Capture same-window record links from shared widgets, including portal content.
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = (event.target as Element)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return;
      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin || url.pathname === window.location.pathname) return;
      if (resolveMobileDestination(url.pathname) !== 'supported') {
        event.preventDefault(); event.stopPropagation(); setUnavailableTarget(url.pathname); return;
      }
      if (!guards.current.some((guard) => guard.dirty)) return;
      event.preventDefault();
      event.stopPropagation();
      request(() => navigate(`${url.pathname}${url.search}${url.hash}`));
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [request, navigate]);
  const value = React.useMemo(() => ({ register, request, unavailable: setUnavailableTarget }), [register, request]);
  return <Context.Provider value={value}>
    {children}
    <Dialog open={Boolean(unavailableTarget)} onOpenChange={(open) => { if (!open) setUnavailableTarget(null); }}>
      <DialogContent aria-labelledby={availabilityTitleId} closeButtonClassName="min-h-11 min-w-11"><DialogHeader><DialogTitle id={availabilityTitleId}>{resolveMobileDestination(unavailableTarget || '/') === 'desktop' ? 'Desktop feature' : 'Destination unavailable'}</DialogTitle><DialogDescription>{mobileDestinationMessage(unavailableTarget || '')}</DialogDescription></DialogHeader><Button className="min-h-11" onClick={() => setUnavailableTarget(null)}>Keep working here</Button></DialogContent>
    </Dialog>
    <Dialog open={Boolean(pending)} onOpenChange={(open) => { if (!open && !busy) setPending(null); }}>
      <DialogContent aria-labelledby="mobile-guard-title" className="max-w-sm">
        <DialogHeader><DialogTitle id="mobile-guard-title">{activeGuard?.title ?? 'Unsaved billing changes'}</DialogTitle><DialogDescription>{activeGuard?.description ?? 'Save or discard your billing draft before leaving this service order.'}</DialogDescription></DialogHeader>
        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        <DialogFooter className="gap-2">
          <Button variant="outline" disabled={busy} onClick={() => setPending(null)}>Stay</Button>
          <Button variant="secondary" disabled={busy} onClick={() => void leave(false)}>Discard and Leave</Button>
          <Button disabled={busy} onClick={() => void leave(true)}>{busy ? 'Saving…' : 'Save and Leave'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </Context.Provider>;
}

export function useMobileDraftGuard(guard: DraftGuard) {
  const context = useContext(Context);
  const latest = useRef(guard);
  latest.current = guard;
  useEffect(() => context?.register({
    get dirty() { return latest.current.dirty; }, get saving() { return latest.current.saving; },
    get title() { return latest.current.title; }, get description() { return latest.current.description; }, get saveError() { return latest.current.saveError; },
    save: () => latest.current.save(), discard: () => latest.current.discard(),
  }), [context]);
}

export function useMobileNavigate() {
  const context = useContext(Context);
  const navigate = useNavigate();
  const location = useLocation();
  return useCallback((to: To, options?: NavigateOptions) => {
    const target = resolvePath(to, location.pathname).pathname;
    if (context && resolveMobileDestination(target) !== 'supported') { context.unavailable(target); return; }
    const action = () => navigate(to, options);
    if (context) context.request(action); else action();
  }, [context, navigate, location.pathname]);
}

export function useMobileNavigationAction() {
  const context = useContext(Context);
  return useCallback((action: () => void) => {
    if (context) context.request(action); else action();
  }, [context]);
}
